import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  aeroWorkbenchApi,
  type AeroCampaignOutput,
  type AeroCampaignPlanResponse,
  type AeroFoilMemoryContribution,
  type AeroLiftingLineSolveResponse,
  type AeroSectionSolveRow,
  type AeroSectionSolveResponse,
  type AeroWorkbenchCapabilities,
  type ThreadAeroOperationResult,
  type ThreadAeroGeometryContent,
  type ThreadAeroSectionModelContent,
  type ThreadAeroTarget,
} from "../../api/aeroWorkbench";
import type { AppacGeometry, WingDefinition } from "./api";
import { AircraftObjectivePanel } from "./AircraftObjectivePanel";
import { QuickLiftingLineSummary } from "./QuickLiftingLineSummary";
import { SectionAnalysisPanel, type SectionAnalysisMode } from "./SectionAnalysisPanel";
import { ThreeDComparisonPanel } from "./ThreeDComparisonPanel";
import {
  DEFAULT_SECTION_CAMPAIGN,
  buildSectionCampaign,
  naca4Shape,
  planformShape,
  setPlanformField,
  setStationField,
  setStationNacaShape,
  type Naca4Shape,
  type PlanformField,
  type SectionCampaignControls,
  type SectionCampaignPlan,
} from "./sectionCampaignModel";
import {
  SECTION_PILOT_LIMIT,
  campaignAxesForWing,
  campaignCoverageGroups,
  isRetryableRow,
  mergeSolveResponses,
  pilotSamplesFromPlan,
  samplesForPointIds,
  type CampaignCoverageGroup,
} from "./sectionCampaignBridge";
import {
  aircraftSectionModelPresentation,
  foilMemoryStats,
  sectionRowSource,
} from "./sectionArchitecture";
import { isUuidLike } from "../thread-v4/viewSpecs";
import { WakeScene } from "./WakeScene";
import {
  QUICK_LIFTING_LINE_MAX_PANELS,
  QUICK_LIFTING_LINE_MAX_STATIONS,
  fitQuickLiftingLinePanelBudget,
  wingMetrics,
} from "./wingModel";

export interface SectionCampaignCoverage {
  completedRows?: number;
  trainableRows?: number;
  freshRows?: number;
  cacheHitRows?: number;
  /** Legacy adapter field; interpreted as completed rows, never displayed as cache truth. */
  cachedRows?: number;
  runningRows?: number;
  failedRows?: number;
}

export interface SectionCampaignRequest {
  schemaVersion: 1;
  wing: WingDefinition;
  stationId: string;
  sectionShape: Naca4Shape & { chord: number; twistDeg: number };
  controls: SectionCampaignControls;
  plan: SectionCampaignPlan;
}

export interface SectionCampaignResult {
  campaignId?: string;
  coverage?: SectionCampaignCoverage;
}

export interface SectionCampaignWorkbenchProps {
  wing: WingDefinition;
  selectedStationId: string;
  previewBody: AppacGeometry | null;
  projectId?: string;
  aircraftModelId: string;
  /** Project-owned VSP design whose aerodynamic wing representation is being versioned. */
  aircraftDesignId?: string;
  projectName: string;
  branch: string;
  coverage?: SectionCampaignCoverage;
  onWingChange: (wing: WingDefinition) => void;
  onSelectStation: (id: string) => void;
  onAddStation: () => void;
  onDeleteStation: () => void;
  onQueueCampaign?: (request: SectionCampaignRequest) => Promise<SectionCampaignResult | void> | SectionCampaignResult | void;
}

interface SliderFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  disabled?: boolean;
  testId?: string;
  onChange: (value: number) => void;
}

type CampaignRunMode = "single" | "remaining";
type QuickLiftingLineState = "idle" | "solving" | "updating" | "done" | "warning" | "error";
type WorkbenchAnalysisMode = SectionAnalysisMode | "objective" | "compare-3d";

interface CampaignCoverageBatch {
  firstGroup: number;
  lastGroup: number;
  groupCount: number;
  rowCount: number;
  datasetSplits: string[];
}

interface ThreadAeroLineageTargets {
  campaignId: string;
  geometry: ThreadAeroTarget;
  plan: ThreadAeroTarget;
  sectionEvidence: ThreadAeroTarget;
  sectionModel: ThreadAeroTarget;
  relaxedWake: ThreadAeroTarget;
}

type GeometryWrite = ThreadAeroOperationResult<ThreadAeroGeometryContent>;
type PlanWrite = ThreadAeroOperationResult<AeroCampaignPlanResponse>;
type BatchWrite = ThreadAeroOperationResult<AeroSectionSolveResponse>;
type ModelWrite = ThreadAeroOperationResult<null, ThreadAeroSectionModelContent>;
type WakeWrite = ThreadAeroOperationResult<AeroLiftingLineSolveResponse>;

const CAMPAIGN_OUTPUTS: AeroCampaignOutput[] = [
  { name: "cl", target_normalized_error: 0.025 },
  { name: "cd", target_normalized_error: 0.05 },
  { name: "cm", target_normalized_error: 0.04 },
  { name: "transition_xc", target_normalized_error: 0.06 },
  { name: "separation_xc", target_normalized_error: 0.06 },
  { name: "convergence_quality", target_normalized_error: 0.04 },
];

function randomUuid(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

function stableToken(value: unknown): string {
  const text = JSON.stringify(value);
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function shortVersion(value: string): string {
  return value.slice(0, 8);
}

function createThreadAeroTargets(
  projectName: string,
  branch: string,
  aircraftModelId: string,
): ThreadAeroLineageTargets {
  const campaignId = randomUuid();
  const metadata = { campaign_id: campaignId, ui_branch: branch };
  return {
    campaignId,
    geometry: { logical_id: randomUuid(), name: `${projectName} wing geometry`, metadata },
    plan: { logical_id: randomUuid(), name: `${projectName} section campaign plan`, metadata },
    sectionEvidence: { logical_id: randomUuid(), name: `${projectName} section evidence`, metadata },
    // Campaigns version one aircraft-owned model instead of minting a new
    // logical model whenever an envelope control changes.
    sectionModel: {
      logical_id: aircraftModelId,
      name: "Aircraft section model",
      metadata: {
        aircraft_model_id: aircraftModelId,
        model_scope: "aircraft",
        model_contract: "aircraft-neural-section.v1",
      },
    },
    relaxedWake: { logical_id: randomUuid(), name: `${projectName} relaxed wake run`, metadata },
  };
}

function persistedAircraftWing(
  wing: WingDefinition,
  aircraftDesignId: string | undefined,
  aircraftModelId: string,
): Record<string, unknown> {
  return {
    ...wing,
    thread_aircraft: {
      design_id: aircraftDesignId ?? null,
      section_model_logical_id: aircraftModelId,
      association_schema: "thread.aero-aircraft-association.v1",
    },
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";
}

function solveStats(result: AeroSectionSolveResponse | null) {
  const rows = result?.rows ?? [];
  const completedRows = rows.filter((row) => row.status === "done").length;
  const fitEligibleCompletedRows = rows.filter(
    (row) => row.status === "done" && row.dataset_split !== "validation",
  );
  const trainableRows = fitEligibleCompletedRows.filter(
    (row) => row.prediction?.converged === true
      && row.prediction_receipt?.exact !== false,
  ).length;
  return {
    executedRows: rows.length,
    completedRows,
    trainableRows,
    validationRows: rows.filter(
      (row) => row.status === "done" && row.dataset_split === "validation",
    ).length,
    provisionalRows: rows.filter((row) =>
      row.prediction_receipt?.kind === "global-bounded-ephemeral-interpolation",
    ).length,
    nonConvergedRows: fitEligibleCompletedRows.length - trainableRows,
    freshRows: rows.filter(
      (row) => row.status === "done"
        && row.cached !== true
        && !isProvisionalGlobalInterpolationRow(row),
    ).length,
    cacheHitRows: rows.filter((row) => row.status === "done" && row.cached === true).length,
    failedRows: rows.filter((row) => row.status === "error").length,
  };
}

function isProvisionalGlobalInterpolationRow(row: AeroSectionSolveRow): boolean {
  return row.status === "done"
    && row.prediction_receipt?.kind === "global-bounded-ephemeral-interpolation"
    && row.prediction_receipt.exact === false;
}

function liftingLineRowAlpha(row: AeroSectionSolveRow): number | null {
  const value = row.query?.alpha_eff_deg;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function isBackendUsableLiftingLineRow(row: AeroSectionSolveRow): boolean {
  const query = row.query;
  return row.status === "done"
    && row.prediction?.converged === true
    && row.prediction_receipt?.exact !== false
    && row.dataset_split !== "validation"
    && typeof row.shape_ref === "string"
    && row.shape_ref.length > 0
    && Array.isArray(row.shape_vector)
    && row.shape_vector.length > 0
    && row.shape_vector.every(Number.isFinite)
    && query != null
    && typeof query.station_id === "string"
    && query.station_id.length > 0
    && query.shape_ref === row.shape_ref
    && liftingLineRowAlpha(row) != null
    && typeof query.reynolds === "number"
    && Number.isFinite(query.reynolds)
    && query.reynolds >= 10_000
    && typeof query.mach === "number"
    && Number.isFinite(query.mach)
    && query.mach >= 0;
}

function compact(value: number, digits = 2): string {
  return Number(value.toFixed(digits)).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function SliderField({
  label,
  value,
  min,
  max,
  step,
  unit = "",
  disabled = false,
  testId,
  onChange,
}: SliderFieldProps) {
  return (
    <label className="section-slider-field">
      <span><b>{label}</b><output>{compact(value)}{unit}</output></span>
      <input
        data-testid={testId}
        type="range"
        aria-label={label}
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
}: SliderFieldProps) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const parsed = draft.trim() === "" ? Number.NaN : Number(draft);
  const invalid = !Number.isFinite(parsed) || parsed < min || parsed > max;
  const commit = () => {
    const next = Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : value;
    setDraft(String(next));
    if (next !== value) onChange(next);
  };
  return (
    <label className="section-number-field">
      <span>{label}</span>
      <span>
        <input
          type="number"
          value={draft}
          min={min}
          max={max}
          step={step}
          aria-invalid={invalid || undefined}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit();
            if (event.key === "Escape") setDraft(String(value));
          }}
        />
        {unit ? <em>{unit}</em> : null}
      </span>
    </label>
  );
}

export function SectionCampaignWorkbench({
  wing,
  selectedStationId,
  previewBody,
  projectId,
  aircraftModelId,
  aircraftDesignId,
  projectName,
  branch,
  coverage,
  onWingChange,
  onSelectStation,
  onAddStation,
  onDeleteStation,
  onQueueCampaign,
}: SectionCampaignWorkbenchProps) {
  const [controls, setControls] = useState<SectionCampaignControls>(DEFAULT_SECTION_CAMPAIGN);
  const [dispatchState, setDispatchState] = useState<"idle" | "submitting" | "staged" | "error">("idle");
  const [dispatchMessage, setDispatchMessage] = useState<string | null>(null);
  const [localCoverage, setLocalCoverage] = useState<SectionCampaignCoverage | null>(null);
  const [serverPlan, setServerPlan] = useState<AeroCampaignPlanResponse | null>(null);
  const [pilotResult, setPilotResult] = useState<AeroSectionSolveResponse | null>(null);
  const [solveAttempts, setSolveAttempts] = useState<AeroSectionSolveResponse[]>([]);
  const [nextPointIndex, setNextPointIndex] = useState(0);
  const [retryPointIds, setRetryPointIds] = useState<string[]>([]);
  const [runningPointIds, setRunningPointIds] = useState<string[]>([]);
  const [capabilities, setCapabilities] = useState<AeroWorkbenchCapabilities | null>(null);
  const [shareFoilObservations, setShareFoilObservations] = useState(false);
  const [useGlobalInterpolation, setUseGlobalInterpolation] = useState(false);
  const [foilContributions, setFoilContributions] = useState<AeroFoilMemoryContribution[] | null>(null);
  const [foilContributionCursor, setFoilContributionCursor] = useState<string | null>(null);
  const [foilContributionBusy, setFoilContributionBusy] = useState(false);
  const [foilContributionError, setFoilContributionError] = useState<string | null>(null);
  const [activeRunMode, setActiveRunMode] = useState<CampaignRunMode | null>(null);
  const [stopRequested, setStopRequested] = useState(false);
  const [sectionTrainingRows, setSectionTrainingRows] = useState<AeroSectionSolveRow[]>([]);
  const [quickAlphaDeg, setQuickAlphaDeg] = useState(5);
  const [quickSpeedMps, setQuickSpeedMps] = useState(45);
  const [quickResult, setQuickResult] = useState<AeroLiftingLineSolveResponse | null>(null);
  const [quickSolvedWing, setQuickSolvedWing] = useState<WingDefinition | null>(null);
  const [quickSolvedCondition, setQuickSolvedCondition] = useState<{ alphaDeg: number; speedMps: number } | null>(null);
  const [quickState, setQuickState] = useState<QuickLiftingLineState>("idle");
  const [quickMessage, setQuickMessage] = useState<string | null>(null);
  const [analysisMode, setAnalysisMode] = useState<WorkbenchAnalysisMode>("wake");
  const [lineageTargets, setLineageTargets] = useState<ThreadAeroLineageTargets | null>(null);
  const [campaignGeometryWrite, setCampaignGeometryWrite] = useState<GeometryWrite | null>(null);
  const [planWrite, setPlanWrite] = useState<PlanWrite | null>(null);
  const [batchWrites, setBatchWrites] = useState<BatchWrite[]>([]);
  const [modelWrite, setModelWrite] = useState<ModelWrite | null>(null);
  const [wakeWrite, setWakeWrite] = useState<WakeWrite | null>(null);
  const [wakeGeometryVersionId, setWakeGeometryVersionId] = useState<string | null>(null);
  const [persistenceError, setPersistenceError] = useState<string | null>(null);
  const [modelRetrying, setModelRetrying] = useState(false);
  const [modelPersistenceFailed, setModelPersistenceFailed] = useState(false);
  const runGenerationRef = useRef(0);
  const activeAbortRef = useRef<AbortController | null>(null);
  const stopRequestedRef = useRef(false);
  const quickGenerationRef = useRef(0);
  const quickAbortRef = useRef<AbortController | null>(null);
  const quickAutoRunRef = useRef(false);
  const capabilitiesRequestRef = useRef<Promise<AeroWorkbenchCapabilities> | null>(null);
  const lineageTargetsRef = useRef<ThreadAeroLineageTargets | null>(null);
  const campaignGeometryWriteRef = useRef<GeometryWrite | null>(null);
  const planWriteRef = useRef<PlanWrite | null>(null);
  const batchWritesRef = useRef<BatchWrite[]>([]);
  const modelWriteRef = useRef<ModelWrite | null>(null);
  const canonicalProjectId = isUuidLike(projectId) ? projectId : null;

  const shape = useMemo(() => planformShape(wing), [wing]);
  const metrics = useMemo(() => wingMetrics(wing), [wing]);
  const station = wing.stations.find((candidate) => candidate.id === selectedStationId) ?? wing.stations[0]!;
  const stationIndex = wing.stations.findIndex((candidate) => candidate.id === station.id);
  const airfoil = useMemo(() => naca4Shape(station.airfoil), [station.airfoil]);
  const plan = useMemo(() => buildSectionCampaign(controls), [controls]);
  const uncoveredStations = useMemo(() => wing.stations.flatMap((candidate) => {
    const candidateShape = naca4Shape(candidate.airfoil);
    const misses: string[] = [];
    if (candidateShape.camberPercent < controls.camberMinPercent || candidateShape.camberPercent > controls.camberMaxPercent) misses.push("camber");
    if (candidateShape.camberPercent > 0 && (
      candidateShape.camberPositionTenths / 10 < controls.camberPositionMinXc
      || candidateShape.camberPositionTenths / 10 > controls.camberPositionMaxXc
    )) misses.push("camber x/c");
    if (candidateShape.thicknessPercent < controls.thicknessMinPercent || candidateShape.thicknessPercent > controls.thicknessMaxPercent) misses.push("thickness");
    return misses.length ? [`${candidate.id} (${candidate.airfoil}): ${misses.join(", ")}`] : [];
  }), [controls.camberMaxPercent, controls.camberMinPercent, controls.camberPositionMaxXc, controls.camberPositionMinXc, controls.thicknessMaxPercent, controls.thicknessMinPercent, wing.stations]);
  const resultStats = useMemo(() => solveStats(pilotResult), [pilotResult]);
  const usableSectionRows = useMemo(
    () => sectionTrainingRows.filter(isBackendUsableLiftingLineRow),
    [sectionTrainingRows],
  );
  const usableSectionRowCount = usableSectionRows.length;
  const usableSectionAlphaCount = useMemo(
    () => new Set(usableSectionRows.map(liftingLineRowAlpha).filter((alpha): alpha is number => alpha != null)).size,
    [usableSectionRows],
  );
  const sectionEvidenceReady = usableSectionRowCount >= 8 && usableSectionAlphaCount >= 2;
  const quickLiftingLineReady = sectionEvidenceReady
    && (!canonicalProjectId || (modelWrite != null && !modelPersistenceFailed));
  const activeCoverage = localCoverage ?? coverage ?? {
    completedRows: 0,
    trainableRows: 0,
    freshRows: 0,
    cacheHitRows: 0,
    runningRows: 0,
    failedRows: 0,
  };
  const plannedRowCount = serverPlan?.plan.solve_points.length ?? plan.estimatedRows;
  const completedRows = Math.max(0, Math.min(plannedRowCount, activeCoverage.completedRows ?? activeCoverage.cachedRows ?? 0));
  const trainableRows = Math.max(0, Math.min(completedRows, activeCoverage.trainableRows ?? completedRows));
  const freshRows = Math.max(0, Math.min(completedRows, activeCoverage.freshRows ?? completedRows));
  const cacheHitRows = Math.max(0, Math.min(completedRows, activeCoverage.cacheHitRows ?? 0));
  const runningRows = Math.max(0, Math.min(plannedRowCount, activeCoverage.runningRows ?? 0));
  const failedRows = Math.max(0, activeCoverage.failedRows ?? 0);
  const coveragePercent = plannedRowCount ? Math.round(trainableRows / plannedRowCount * 100) : 0;
  const campaignComplete = !!serverPlan
    && nextPointIndex >= serverPlan.plan.solve_points.length
    && retryPointIds.length === 0;
  const remainingRows = serverPlan ? Math.max(0, serverPlan.plan.solve_points.length - nextPointIndex) : 0;
  const batchLimit = Math.max(
    1,
    Math.min(SECTION_PILOT_LIMIT, Math.floor(capabilities?.section_solver.max_points_per_request ?? SECTION_PILOT_LIMIT)),
  );
  const previousStation = wing.stations[stationIndex - 1];
  const nextStation = wing.stations[stationIndex + 1];
  const stationYMin = previousStation ? previousStation.y + 0.01 : station.y;
  const stationYMax = nextStation ? nextStation.y - 0.01 : station.y;
  const quickSolverWing = useMemo(() => fitQuickLiftingLinePanelBudget(wing), [wing]);
  const estimatedQuickPanels = 2 * Math.max(0, wing.stations.length - 1) * quickSolverWing.span_panels_per_segment;
  const quickStationLimit = Math.floor(
    (QUICK_LIFTING_LINE_MAX_PANELS / 2) / Math.max(1, wing.span_panels_per_segment),
  ) + 1;

  const invalidateActiveRun = () => {
    runGenerationRef.current += 1;
    activeAbortRef.current?.abort();
    activeAbortRef.current = null;
    stopRequestedRef.current = true;
  };

  const loadCapabilities = useCallback((): Promise<AeroWorkbenchCapabilities> => {
    if (capabilitiesRequestRef.current) return capabilitiesRequestRef.current;
    const pending = aeroWorkbenchApi.capabilities();
    capabilitiesRequestRef.current = pending;
    void pending.catch(() => {
      if (capabilitiesRequestRef.current === pending) capabilitiesRequestRef.current = null;
    });
    return pending;
  }, []);

  useEffect(() => {
    let active = true;
    void loadCapabilities().then((response) => {
      if (active) setCapabilities(response);
    }).catch(() => {
      // Capabilities are advisory; concrete operations still return errors.
    });
    return () => { active = false; };
  }, [loadCapabilities]);

  useEffect(() => {
    if (capabilities?.foil_memory?.approximate_predictions !== true) {
      setUseGlobalInterpolation(false);
    }
  }, [capabilities?.foil_memory?.approximate_predictions]);

  const ensureLineageTargets = (): ThreadAeroLineageTargets => {
    if (lineageTargetsRef.current) return lineageTargetsRef.current;
    const targets = createThreadAeroTargets(
      projectName,
      branch,
      aircraftModelId,
    );
    lineageTargetsRef.current = targets;
    setLineageTargets(targets);
    return targets;
  };

  const recordCampaignGeometryWrite = (write: GeometryWrite | null) => {
    campaignGeometryWriteRef.current = write;
    setCampaignGeometryWrite(write);
  };

  const recordPlanWrite = (write: PlanWrite | null) => {
    planWriteRef.current = write;
    setPlanWrite(write);
  };

  const recordBatchWrites = (writes: BatchWrite[]) => {
    batchWritesRef.current = writes;
    setBatchWrites(writes);
  };

  const recordModelWrite = (write: ModelWrite | null) => {
    modelWriteRef.current = write;
    setModelWrite(write);
  };

  const resetThreadLineage = () => {
    lineageTargetsRef.current = null;
    campaignGeometryWriteRef.current = null;
    planWriteRef.current = null;
    batchWritesRef.current = [];
    modelWriteRef.current = null;
    setLineageTargets(null);
    setCampaignGeometryWrite(null);
    setPlanWrite(null);
    setBatchWrites([]);
    setModelWrite(null);
    setWakeWrite(null);
    setWakeGeometryVersionId(null);
    setPersistenceError(null);
    setModelRetrying(false);
    setModelPersistenceFailed(false);
  };

  // The 2D evidence is a global section-domain artifact. Planform, station
  // topology and station selection are downstream lifting-line inputs and do
  // not invalidate those expensive rows.
  useEffect(() => {
    invalidateActiveRun();
    setServerPlan(null);
    setPilotResult(null);
    setSolveAttempts([]);
    setNextPointIndex(0);
    setRetryPointIds([]);
    setRunningPointIds([]);
    setLocalCoverage(null);
    setDispatchState("idle");
    setDispatchMessage(null);
    setActiveRunMode(null);
    setStopRequested(false);
    setSectionTrainingRows([]);
    quickGenerationRef.current += 1;
    quickAbortRef.current?.abort();
    quickAbortRef.current = null;
    quickAutoRunRef.current = false;
    setQuickResult(null);
    setQuickSolvedWing(null);
    setQuickSolvedCondition(null);
    setQuickState("idle");
    setQuickMessage(null);
    resetThreadLineage();
  }, [aircraftDesignId, aircraftModelId, branch, controls, projectId]);

  useEffect(() => () => {
    runGenerationRef.current += 1;
    activeAbortRef.current?.abort();
    quickGenerationRef.current += 1;
    quickAbortRef.current?.abort();
  }, []);

  const setPlanform = (field: PlanformField, value: number) => {
    onWingChange(setPlanformField(wing, field, value));
  };

  const setNaca = (patch: Partial<Naca4Shape>) => {
    onWingChange(setStationNacaShape(wing, station.id, patch));
  };

  const setStation = (field: "y" | "x_le" | "z_le" | "chord" | "twist_deg", value: number) => {
    onWingChange(setStationField(wing, station.id, field, value));
  };

  const addStation = () => {
    onAddStation();
  };

  const deleteStation = () => {
    onDeleteStation();
  };

  const setControl = <K extends keyof SectionCampaignControls>(key: K, value: SectionCampaignControls[K]) => {
    invalidateActiveRun();
    setControls((current) => ({ ...current, [key]: value }));
    setDispatchState("idle");
    setDispatchMessage(null);
  };

  const persistCurrentSectionModel = async (signal?: AbortSignal): Promise<ModelWrite> => {
    if (!canonicalProjectId) throw new Error("A persisted Thread project is required.");
    const targets = lineageTargetsRef.current;
    const persistedPlan = planWriteRef.current;
    const evidenceArtifactVersionIds = batchWritesRef.current.map((write) => write.artifact_version_id);
    if (!targets || !persistedPlan || evidenceArtifactVersionIds.length === 0) {
      throw new Error("Canonical campaign plan and section evidence are required before fitting the aircraft section model.");
    }
    const fitted = await aeroWorkbenchApi.fitThreadSectionModel({
      project_id: canonicalProjectId,
      idempotency_key: `aero:${targets.campaignId}:aircraft-section-nn-v1:${stableToken(evidenceArtifactVersionIds)}`,
      target: targets.sectionModel,
      campaign_plan_artifact_version_id: persistedPlan.artifact_version_id,
      evidence_artifact_version_ids: evidenceArtifactVersionIds,
      max_fit_rows: 8192,
    }, signal);
    recordModelWrite(fitted);
    setPersistenceError(null);
    setModelPersistenceFailed(false);
    return fitted;
  };

  const queueCampaign = async (mode: CampaignRunMode = "single") => {
    const request: SectionCampaignRequest = {
      schemaVersion: 1,
      wing,
      stationId: station.id,
      sectionShape: {
        ...airfoil,
        chord: station.chord,
        twistDeg: station.twist_deg,
      },
      controls,
      plan,
    };
    activeAbortRef.current?.abort();
    const controller = new AbortController();
    activeAbortRef.current = controller;
    const generation = runGenerationRef.current + 1;
    runGenerationRef.current = generation;
    const assertActive = () => {
      if (controller.signal.aborted || runGenerationRef.current !== generation) {
        throw new DOMException("Campaign run superseded", "AbortError");
      }
    };
    stopRequestedRef.current = false;
    setStopRequested(false);
    setActiveRunMode(mode);
    setDispatchState("submitting");
    setDispatchMessage(null);
    setPersistenceError(null);
    try {
      if (!canonicalProjectId && onQueueCampaign) {
        const result = await onQueueCampaign(request);
        assertActive();
        if (result?.coverage) setLocalCoverage(result.coverage);
        setDispatchMessage(result?.campaignId
          ? `Campaign ${result.campaignId} queued through the section-solver adapter.`
          : `Plan staged: ${plan.groupedAlphaSweeps} Halton-grouped α sweeps are ready for the fast 2D solver adapter.`);
      } else {
        const targets = canonicalProjectId ? ensureLineageTargets() : null;
        let planned = serverPlan;
        let batchStart = nextPointIndex;
        let accumulated = pilotResult;
        let attempts = [...solveAttempts];
        let pendingRetries = [...retryPointIds];
        let persistedBatches = [...batchWritesRef.current];
        // The plan and every continuation batch must share the exact geometry
        // artifact version captured when the plan was created. Quick-wake and
        // inverse-design runs persist coarsened solver geometry independently.
        let persistedGeometry = campaignGeometryWriteRef.current;
        let persistedPlan = planWriteRef.current;
        let resolvedCapabilities = capabilities;
        if (!resolvedCapabilities) {
          try {
            resolvedCapabilities = await loadCapabilities();
            assertActive();
            if (resolvedCapabilities?.section_solver) setCapabilities(resolvedCapabilities);
          } catch (error) {
            if (isAbortError(error)) throw error;
            // Staggered deployments may not expose capabilities yet. Keep the
            // conservative local cap and let the concrete solve report errors.
            resolvedCapabilities = null;
          }
        }
        const requestLimit = Math.max(
          1,
          Math.min(
            SECTION_PILOT_LIMIT,
            Math.floor(resolvedCapabilities?.section_solver.max_points_per_request ?? SECTION_PILOT_LIMIT),
          ),
        );
        if (!planned) {
          const planRequest = {
            axes: campaignAxesForWing(wing, controls),
            outputs: CAMPAIGN_OUTPUTS,
            base_design_points: Math.min(10_000, Math.max(1, Math.round(controls.baseDesignPoints))),
            alpha_samples: Math.min(101, Math.max(2, Math.round(controls.alphaSamples))),
            failure_rate: 0.12,
          };
          if (canonicalProjectId && targets) {
            const geometryToken = stableToken(
              persistedAircraftWing(wing, aircraftDesignId, aircraftModelId),
            );
            persistedGeometry = await aeroWorkbenchApi.saveThreadGeometry({
              project_id: canonicalProjectId,
              idempotency_key: `aero:${targets.campaignId}:geometry:${geometryToken}`,
              target: targets.geometry,
              wing: persistedAircraftWing(wing, aircraftDesignId, aircraftModelId),
            }, controller.signal);
            assertActive();
            recordCampaignGeometryWrite(persistedGeometry);
            persistedPlan = await aeroWorkbenchApi.createThreadCampaignPlan({
              project_id: canonicalProjectId,
              idempotency_key: `aero:${targets.campaignId}:campaign-plan`,
              target: targets.plan,
              geometry_artifact_version_id: persistedGeometry.artifact_version_id,
              plan_request: planRequest,
            }, controller.signal);
            assertActive();
            recordPlanWrite(persistedPlan);
            planned = persistedPlan.content;
          } else {
            await aeroWorkbenchApi.wingBundle(wing as unknown as Record<string, unknown>, controller.signal);
            assertActive();
            planned = await aeroWorkbenchApi.campaignPlan(planRequest, controller.signal);
          }
          assertActive();
          setServerPlan(planned);
          batchStart = 0;
          accumulated = null;
          attempts = [];
          pendingRetries = [];
          persistedBatches = [];
          recordBatchWrites([]);
          recordModelWrite(null);
        } else if (canonicalProjectId && (!persistedGeometry || !persistedPlan)) {
          throw new Error("Thread campaign lineage is incomplete; change a campaign control to start a fresh persisted campaign.");
        }
        const autoRetryIds = mode === "remaining" ? new Set(pendingRetries) : null;
        let batchCount = 0;
        while (true) {
          assertActive();
          const eligibleRetryIds = pendingRetries.filter((pointId) => !autoRetryIds || autoRetryIds.has(pointId));
          const retrySamples = eligibleRetryIds.length
            ? samplesForPointIds(planned, eligibleRetryIds, requestLimit)
            : [];
          for (const sample of retrySamples) autoRetryIds?.delete(sample.point_id);
          const newCapacity = requestLimit - retrySamples.length;
          const newSamples = newCapacity > 0 && batchStart < planned.plan.solve_points.length
            ? pilotSamplesFromPlan(planned, newCapacity, batchStart)
            : [];
          const retryIds = new Set(retrySamples.map((sample) => sample.point_id));
          const pilot = [...retrySamples, ...newSamples.filter((sample) => !retryIds.has(sample.point_id))];
          if (!pilot.length) break;
          // A provisional global interpolation is useful immediately, but it
          // cannot become aircraft-NN evidence. Its queued follow-up bypasses
          // approximation and buys/recovers an exact solver observation.
          const allowGlobalInterpolationForBatch = useGlobalInterpolation
            && retrySamples.length === 0;

          const before = solveStats(accumulated);
          setLocalCoverage({
            completedRows: before.completedRows,
            trainableRows: before.trainableRows,
            freshRows: before.freshRows,
            cacheHitRows: before.cacheHitRows,
            runningRows: pilot.length,
            failedRows: before.failedRows,
          });
          setRunningPointIds(pilot.map((sample) => sample.point_id));
          let batchWrite: BatchWrite | null = null;
          let solved: AeroSectionSolveResponse;
          if (canonicalProjectId && targets && persistedGeometry && persistedPlan) {
            batchWrite = await aeroWorkbenchApi.solveThreadSectionBatch({
              project_id: canonicalProjectId,
              idempotency_key: `aero:${targets.campaignId}:section-batch:${persistedBatches.length}`,
              target: targets.sectionEvidence,
              geometry_artifact_version_id: persistedGeometry.artifact_version_id,
              campaign_plan_artifact_version_id: persistedPlan.artifact_version_id,
              solve_request: {
                samples: pilot,
                ncrit: 9,
                max_iterations: 120,
                observation_scope: shareFoilObservations ? "shared" : "private",
                allow_global_interpolation: allowGlobalInterpolationForBatch,
              },
            }, controller.signal);
            solved = batchWrite.content;
          } else {
            solved = await aeroWorkbenchApi.solveSections(
              pilot,
              120,
              controller.signal,
              shareFoilObservations ? "shared" : "private",
              allowGlobalInterpolationForBatch,
            );
          }
          assertActive();
          const splitById = new Map(pilot.map((sample) => [sample.point_id, sample.dataset_split]));
          const enriched: AeroSectionSolveResponse = {
            ...solved,
            rows: solved.rows.map((row) => ({
              ...row,
              dataset_split: row.dataset_split ?? splitById.get(row.point_id),
            })),
          };
          attempts = [...attempts, enriched];
          accumulated = mergeSolveResponses(accumulated, enriched);
          if (batchWrite && !persistedBatches.some((write) => write.artifact_version_id === batchWrite.artifact_version_id)) {
            persistedBatches = [...persistedBatches, batchWrite];
            recordBatchWrites(persistedBatches);
            // A model is executable evidence, not a generic cache entry. As
            // soon as the evidence frontier advances, the prior version is
            // stale for canonical wake runs until the new checkpoint is fit.
            recordModelWrite(null);
            setWakeWrite(null);
            setWakeGeometryVersionId(null);
            quickGenerationRef.current += 1;
            quickAbortRef.current?.abort();
            quickAbortRef.current = null;
          }
          batchStart += newSamples.length;
          const attemptedIds = new Set(pilot.map((sample) => sample.point_id));
          pendingRetries = pendingRetries.filter((pointId) => !attemptedIds.has(pointId));
          for (const row of enriched.rows) {
            if (isRetryableRow(row) && !pendingRetries.includes(row.point_id)) pendingRetries.push(row.point_id);
            if (isProvisionalGlobalInterpolationRow(row) && !pendingRetries.includes(row.point_id)) {
              pendingRetries.push(row.point_id);
              autoRetryIds?.add(row.point_id);
            }
          }
          batchCount += 1;

          const after = solveStats(accumulated);
          setPilotResult(accumulated);
          setSectionTrainingRows(accumulated.rows);
          setSolveAttempts(attempts);
          setNextPointIndex(batchStart);
          setRetryPointIds(pendingRetries);
          setRunningPointIds([]);
          setLocalCoverage({
            completedRows: after.completedRows,
            trainableRows: after.trainableRows,
            freshRows: after.freshRows,
            cacheHitRows: after.cacheHitRows,
            runningRows: 0,
            failedRows: after.failedRows,
          });
          const newRowsRemaining = planned.plan.solve_points.length - batchStart;
          setDispatchMessage(
            `Batch ${batchCount} complete via ${enriched.solver}: ${after.trainableRows} trainable, `
            + `${after.completedRows} completed, ${after.executedRows} executed. `
            + `${newRowsRemaining} unattempted and ${pendingRetries.length} exact/retry follow-ups remain.`,
          );

          const hasEligibleRetry = pendingRetries.some((pointId) => autoRetryIds?.has(pointId));
          const willContinue = mode === "remaining"
            && !stopRequestedRef.current
            && (batchStart < planned.plan.solve_points.length || hasEligibleRetry);
          if (canonicalProjectId && targets && persistedPlan && !willContinue) {
            const modelRows = accumulated.rows.filter(isBackendUsableLiftingLineRow);
            const modelAlphaCount = new Set(
              modelRows.map(liftingLineRowAlpha).filter((alpha): alpha is number => alpha != null),
            ).size;
            if (modelRows.length >= 8 && modelAlphaCount >= 2) {
              try {
                await persistCurrentSectionModel(controller.signal);
                assertActive();
              } catch (error) {
                if (isAbortError(error)) throw error;
                const detail = error instanceof Error ? error.message : "unknown persistence error";
                const message = `Section batch ${batchWrite ? shortVersion(batchWrite.artifact_version_id) : "saved"} is preserved, but aircraft model persistence failed: ${detail}`;
                setPersistenceError(message);
                setModelPersistenceFailed(true);
                throw new Error(message);
              }
            }
          }

          if (mode === "single" || stopRequestedRef.current) break;
          if (batchStart >= planned.plan.solve_points.length && !hasEligibleRetry) break;
        }

        assertActive();
        const finalStats = solveStats(accumulated);
        const newRowsRemaining = planned.plan.solve_points.length - batchStart;
        if (stopRequestedRef.current) {
          setDispatchMessage(
            `Paused after the current bounded batch. ${newRowsRemaining} unattempted and `
            + `${pendingRetries.length} retryable rows remain.`,
          );
        } else if (newRowsRemaining === 0 && pendingRetries.length === 0) {
          setDispatchMessage(
            `Campaign complete: ${finalStats.trainableRows} trainable of ${planned.plan.solve_points.length} planned rows; `
            + `${finalStats.nonConvergedRows} non-converged and ${finalStats.failedRows} solver failures retained.`,
          );
        } else if (newRowsRemaining === 0 && pendingRetries.length > 0) {
          setDispatchMessage(
            `Every planned row has been attempted; ${pendingRetries.length} exact/retry `
            + `${pendingRetries.length === 1 ? "follow-up remains" : "follow-ups remain"}.`,
          );
        }
      }
      assertActive();
      setDispatchState("staged");
    } catch (error) {
      if (isAbortError(error) || runGenerationRef.current !== generation) return;
      setRunningPointIds([]);
      setLocalCoverage((current) => current ? { ...current, runningRows: 0 } : current);
      setDispatchState("error");
      setDispatchMessage(error instanceof Error ? error.message : "Section campaign submission failed");
    } finally {
      if (runGenerationRef.current === generation) {
        activeAbortRef.current = null;
        stopRequestedRef.current = false;
        setStopRequested(false);
        setActiveRunMode(null);
        setRunningPointIds([]);
      }
    }
  };

  const requestAutoStop = () => {
    stopRequestedRef.current = true;
    setStopRequested(true);
    setDispatchMessage("Pause requested; the current bounded batch will finish safely.");
  };

  const retryModelPersistence = async () => {
    if (!canonicalProjectId || !sectionEvidenceReady || modelRetrying) return;
    activeAbortRef.current?.abort();
    const controller = new AbortController();
    activeAbortRef.current = controller;
    const generation = runGenerationRef.current + 1;
    runGenerationRef.current = generation;
    setModelRetrying(true);
    setDispatchState("submitting");
    setPersistenceError(null);
    try {
      const fitted = await persistCurrentSectionModel(controller.signal);
      if (controller.signal.aborted || generation !== runGenerationRef.current) return;
      setDispatchState("staged");
      setDispatchMessage(`Aircraft section model saved as version ${shortVersion(fitted.artifact_version_id)}. Quick 3D is ready.`);
    } catch (error) {
      if (isAbortError(error) || generation !== runGenerationRef.current) return;
      const detail = error instanceof Error ? error.message : "unknown persistence error";
      const message = `Section evidence is preserved, but aircraft model persistence failed: ${detail}`;
      setPersistenceError(message);
      setModelPersistenceFailed(true);
      setDispatchState("error");
      setDispatchMessage(message);
    } finally {
      if (generation === runGenerationRef.current) {
        activeAbortRef.current = null;
        setModelRetrying(false);
      }
    }
  };

  const loadFoilContributions = async (afterContributionId?: string) => {
    setFoilContributionBusy(true);
    setFoilContributionError(null);
    try {
      const response = await aeroWorkbenchApi.foilMemoryContributions(
        false,
        undefined,
        afterContributionId,
      );
      setFoilContributions((current) => afterContributionId && current
        ? [...current, ...response.contributions]
        : response.contributions);
      setFoilContributionCursor(response.next_cursor);
    } catch (error) {
      setFoilContributionError(
        error instanceof Error ? error.message : "Could not load shared contributions",
      );
    } finally {
      setFoilContributionBusy(false);
    }
  };

  const withdrawFoilContribution = async (contributionId: string) => {
    setFoilContributionBusy(true);
    setFoilContributionError(null);
    try {
      await aeroWorkbenchApi.revokeFoilMemoryContribution(contributionId);
      setFoilContributions((current) => current?.filter(
        (contribution) => contribution.contribution_id !== contributionId,
      ) ?? []);
    } catch (error) {
      setFoilContributionError(
        error instanceof Error ? error.message : "Could not withdraw contribution",
      );
    } finally {
      setFoilContributionBusy(false);
    }
  };

  const downloadPilotJson = () => {
    if (!pilotResult) return;
    const payload = JSON.stringify({
      schema_version: "thread.aero-section-pilot-export.v1",
      exported_at: new Date().toISOString(),
      project: projectName,
      branch,
      station_id: station.id,
      campaign_plan: serverPlan,
      section_results: pilotResult,
      solve_attempts: solveAttempts,
      retry_queue: retryPointIds,
    }, null, 2);
    const anchor = document.createElement("a");
    const safeProject = projectName.trim().replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "aero";
    anchor.download = `${safeProject}-section-pilot.json`;
    if (typeof URL.createObjectURL === "function") {
      const url = URL.createObjectURL(new Blob([payload], { type: "application/json" }));
      anchor.href = url;
      anchor.click();
      URL.revokeObjectURL(url);
    } else {
      anchor.href = `data:application/json;charset=utf-8,${encodeURIComponent(payload)}`;
      anchor.click();
    }
  };

  const runQuickLiftingLine = useCallback(async (targetWing: WingDefinition, automatic = false) => {
    if (!quickLiftingLineReady) {
      setQuickState("error");
      setQuickMessage("Run at least eight converged 2D rows spanning two angles before solving the lifting line.");
      return;
    }
    quickAbortRef.current?.abort();
    const controller = new AbortController();
    quickAbortRef.current = controller;
    const generation = quickGenerationRef.current + 1;
    quickGenerationRef.current = generation;
    setQuickState((current) => automatic || current === "done" || current === "warning" || current === "updating" ? "updating" : "solving");
    setQuickMessage(automatic ? "Geometry changed; updating the relaxed free wake…" : null);
    setPersistenceError(null);
    setModelPersistenceFailed(false);
    try {
      const solverWing = fitQuickLiftingLinePanelBudget(targetWing);
      const condition = {
        speed_mps: quickSpeedMps,
        alpha_deg: quickAlphaDeg,
        beta_deg: 0,
        density_kg_m3: 1.225,
        dynamic_viscosity_pa_s: 1.81e-5,
        speed_of_sound_mps: 340,
      };
      const settings = {
        max_iterations: 400,
        relaxation: 0.15,
        relative_tolerance: 1e-5,
        vortex_core_radius_m: 0.05,
        wake_length_m: Math.max(20, planformShape(targetWing).halfSpan * 3),
        include_mirror_y: true,
      };
      let response: AeroLiftingLineSolveResponse;
      if (canonicalProjectId) {
        const targets = lineageTargetsRef.current;
        const fittedModel = modelWriteRef.current;
        if (!targets || !fittedModel) {
          throw new Error("The persisted aircraft section model is not ready; run another section batch first.");
        }
        const geometryToken = stableToken(
          persistedAircraftWing(solverWing, aircraftDesignId, aircraftModelId),
        );
        const persistedGeometry = await aeroWorkbenchApi.saveThreadGeometry({
          project_id: canonicalProjectId,
          idempotency_key: `aero:${targets.campaignId}:geometry:${geometryToken}`,
          target: targets.geometry,
          wing: persistedAircraftWing(solverWing, aircraftDesignId, aircraftModelId),
        }, controller.signal);
        if (controller.signal.aborted || generation !== quickGenerationRef.current) return;
        const persistedWake = await aeroWorkbenchApi.runThreadRelaxedWake({
          project_id: canonicalProjectId,
          idempotency_key: `aero:${targets.campaignId}:relaxed-wake:${stableToken({
            geometry: persistedGeometry.artifact_version_id,
            model: fittedModel.artifact_version_id,
            condition,
            settings,
          })}`,
          target: targets.relaxedWake,
          geometry_artifact_version_id: persistedGeometry.artifact_version_id,
          section_model_artifact_version_id: fittedModel.artifact_version_id,
          condition,
          settings,
        }, controller.signal);
        if (controller.signal.aborted || generation !== quickGenerationRef.current) return;
        setWakeWrite(persistedWake);
        setWakeGeometryVersionId(persistedGeometry.artifact_version_id);
        response = persistedWake.content;
      } else {
        response = await aeroWorkbenchApi.solveLiftingLine({
          wing: solverWing as unknown as Record<string, unknown>,
          // Retain failures and non-convergence as first-class evidence. The
          // backend filters coefficient-fit rows without hiding those records.
          section_rows: sectionTrainingRows,
          aircraft_model_instance_id: aircraftModelId,
          input_slider_names: campaignAxesForWing(targetWing, controls)
            .filter((axis) => axis.role === "section_shape")
            .map((axis) => axis.name),
          condition,
          settings,
        }, controller.signal);
      }
      if (controller.signal.aborted || generation !== quickGenerationRef.current) return;
      if (
        response.schema_version !== "aero.lifting-line-result.v2"
        || response.prescribed_wake !== false
        || response.wake_model.kind !== "relaxed-free-wake"
        || response.wake_model.free_wake !== true
        || !Array.isArray(response.wake_filaments)
      ) {
        throw new Error("Quick solver returned an unsupported wake model");
      }
      setQuickResult(response);
      setQuickSolvedWing({
        ...targetWing,
        stations: targetWing.stations.map((candidate) => ({ ...candidate })),
      });
      setQuickSolvedCondition({ alphaDeg: quickAlphaDeg, speedMps: quickSpeedMps });
      setQuickState(response.result.valid ? "done" : "warning");
      const fittedRows = response.section_model.trainable_row_count
        ?? response.section_model.training_row_count
        ?? usableSectionRowCount;
      setQuickMessage(response.result.valid
        ? `Solved ${response.wake_filaments.length} relaxed wake panels from an aircraft section model fitted to ${fittedRows} rows.`
        : response.result.messages.join(" · ") || "Lifting-line result is not valid.");
    } catch (error) {
      if (isAbortError(error) || generation !== quickGenerationRef.current) return;
      setQuickState("error");
      const detail = error instanceof Error ? error.message : "Quick lifting-line solve failed";
      if (canonicalProjectId) {
        const message = `Thread quick-3D run failed; no successful result was replaced: ${detail}`;
        setPersistenceError(message);
        setQuickMessage(message);
      } else {
        setQuickMessage(detail);
      }
    } finally {
      if (generation === quickGenerationRef.current) quickAbortRef.current = null;
    }
  }, [aircraftDesignId, aircraftModelId, canonicalProjectId, controls, quickAlphaDeg, quickLiftingLineReady, quickSpeedMps, sectionTrainingRows, usableSectionRowCount]);

  // Once the user has requested a quick solve, geometry/condition edits keep
  // the flowfield live. Debouncing avoids one HTTP solve for every slider tick.
  useEffect(() => {
    if (!quickAutoRunRef.current || !quickLiftingLineReady) return;
    quickGenerationRef.current += 1;
    quickAbortRef.current?.abort();
    quickAbortRef.current = null;
    setQuickState("updating");
    setQuickMessage("Geometry or flight condition changed; updating the relaxed free wake…");
    const timer = window.setTimeout(() => {
      void runQuickLiftingLine(wing, true);
    }, 320);
    return () => window.clearTimeout(timer);
  }, [quickAlphaDeg, quickLiftingLineReady, quickSpeedMps, runQuickLiftingLine, usableSectionRowCount, wing]);

  const requestQuickLiftingLine = () => {
    quickAutoRunRef.current = true;
    void runQuickLiftingLine(wing);
  };

  const prepareThreadInverseDesign = async (signal: AbortSignal) => {
    if (!canonicalProjectId) throw new Error("Inverse design is not inside a persisted Thread project");
    const fittedModel = modelWriteRef.current;
    if (!fittedModel) throw new Error("The canonical aircraft section model is not ready");
    const targets = ensureLineageTargets();
    const solverWing = fitQuickLiftingLinePanelBudget(wing);
    const persistedWing = persistedAircraftWing(solverWing, aircraftDesignId, aircraftModelId);
    const persistedGeometry = await aeroWorkbenchApi.saveThreadGeometry({
      project_id: canonicalProjectId,
      idempotency_key: `aero:${targets.campaignId}:geometry:${stableToken(persistedWing)}`,
      target: targets.geometry,
      wing: persistedWing,
    }, signal);
    return {
      projectId: canonicalProjectId,
      geometryArtifactVersionId: persistedGeometry.artifact_version_id,
      sectionModelArtifactVersionId: fittedModel.artifact_version_id,
    };
  };

  const coverageGroups = useMemo<CampaignCoverageGroup[]>(() => serverPlan
    ? campaignCoverageGroups(serverPlan)
    : plan.groups.map((group) => ({
        id: group.id,
        designIndex: group.designIndex,
        pointIds: [],
        rowCount: group.rowCount,
        datasetSplit: "unassigned",
      })), [plan.groups, serverPlan]);
  const coverageGroupsWithOffsets = useMemo(() => {
    let startRow = 0;
    return coverageGroups.map((group) => {
      const entry = { ...group, startRow };
      startRow += group.rowCount;
      return entry;
    });
  }, [coverageGroups]);
  const coverageBatches = useMemo<CampaignCoverageBatch[]>(() => {
    if (!serverPlan) {
      return plan.haltonBatches.map((summary) => ({ ...summary, datasetSplits: [] }));
    }
    const batchSize = 12;
    return Array.from({ length: Math.ceil(coverageGroups.length / batchSize) }, (_, batchIndex) => {
      const groups = coverageGroups.slice(batchIndex * batchSize, (batchIndex + 1) * batchSize);
      const datasetSplits = [...new Set(groups
        .map((group) => group.datasetSplit)
        .filter((split) => split !== "unassigned"))];
      return {
        firstGroup: (groups[0]?.designIndex ?? batchIndex * batchSize) + 1,
        lastGroup: (groups[groups.length - 1]?.designIndex ?? batchIndex * batchSize + groups.length - 1) + 1,
        groupCount: groups.length,
        rowCount: groups.reduce((total, group) => total + group.rowCount, 0),
        datasetSplits,
      };
    });
  }, [coverageGroups, plan.haltonBatches, serverPlan]);
  const resultRowsByPointId = useMemo(
    () => new Map((pilotResult?.rows ?? []).map((row) => [row.point_id, row])),
    [pilotResult],
  );
  const runningPointIdSet = useMemo(() => new Set(runningPointIds), [runningPointIds]);
  const datasetRowSummary = useMemo(() => {
    if (!serverPlan) return [];
    const derived = new Map<string, number>();
    for (const group of coverageGroups) {
      const split = group.datasetSplit === "train" ? "training" : group.datasetSplit;
      derived.set(split, (derived.get(split) ?? 0) + group.rowCount);
    }
    if (serverPlan.plan.training_2d_solve_count != null) {
      derived.set("training", serverPlan.plan.training_2d_solve_count);
    }
    if (serverPlan.plan.validation_2d_solve_count != null) {
      derived.set("validation", serverPlan.plan.validation_2d_solve_count);
    }
    return [...derived.entries()].filter(([split, count]) => split !== "unassigned" && count > 0);
  }, [coverageGroups, serverPlan]);
  const displayedGroupCount = serverPlan?.plan.actual_alpha_sweep_count ?? plan.groupedAlphaSweeps;
  const displayedRowsPerSweep = serverPlan?.plan.alpha_samples_per_sweep ?? plan.alphaValues.length;
  const predictedRows = pilotResult?.rows.filter((row) => row.status === "done" && row.prediction) ?? [];
  const memoryStats = useMemo(() => foilMemoryStats(pilotResult), [pilotResult]);
  const modelPresentation = useMemo(() => aircraftSectionModelPresentation(
    quickResult?.section_model ?? modelWrite?.content_summary,
  ), [modelWrite?.content_summary, quickResult?.section_model]);
  const foilMemoryStatus = !pilotResult
    ? "lookup on first run"
    : memoryStats.hasReceiptContract
      ? `${memoryStats.globalExactHits} global exact${memoryStats.globalApproximateHits ? ` · ${memoryStats.globalApproximateHits} global approximate` : ""}${memoryStats.exactReuseHits ? ` · ${memoryStats.exactReuseHits} scoped exact` : ""} · ${memoryStats.solverRuns} solved${memoryStats.publicationFailures ? ` · ${memoryStats.publicationFailures} not stored` : ""}`
      : `${memoryStats.legacyExactHits} legacy exact · ${memoryStats.solverRuns} solved`;
  const sharedFoilMemoryConfigured = capabilities?.foil_memory?.shared_store_configured === true;
  const globalInterpolationConfigured = capabilities?.foil_memory?.approximate_predictions === true;
  const sharedFoilPublicationEnabled = capabilities?.foil_memory?.shared_publish_enabled === true;
  const aircraftModelStatus = modelPresentation.label !== "not fitted"
    ? `${modelPresentation.label}${modelWrite ? ` · v${shortVersion(modelWrite.artifact_version_id)}` : ""}`
    : modelPersistenceFailed
      ? "save failed"
      : sectionEvidenceReady
        ? canonicalProjectId ? "fitting from evidence" : "fits ephemerally on run"
        : "needs section evidence";
  const nextSingleBatchSize = serverPlan
    ? Math.min(batchLimit, retryPointIds.length + remainingRows)
    : Math.min(batchLimit, plan.estimatedRows);
  const usesExternalAdapter = !canonicalProjectId && onQueueCampaign != null;
  const singleRunLabel = usesExternalAdapter
    ? "Queue campaign"
    : campaignComplete
      ? "Campaign complete"
      : serverPlan && retryPointIds.length > 0 && remainingRows === 0
        ? `Retry ${Math.min(batchLimit, retryPointIds.length)} transient rows`
        : serverPlan
          ? `Run next ${nextSingleBatchSize} rows${retryPointIds.length ? ` · ${Math.min(batchLimit, retryPointIds.length)} retry` : ""}`
          : `Run first batch · up to ${nextSingleBatchSize} rows`;
  const latestBatchWrite = batchWrites[batchWrites.length - 1] ?? null;
  const latestLineageVersionId = wakeWrite?.artifact_version_id
    ?? modelWrite?.artifact_version_id
    ?? latestBatchWrite?.artifact_version_id
    ?? planWrite?.artifact_version_id
    ?? campaignGeometryWrite?.artifact_version_id
    ?? null;
  const lineageHref = canonicalProjectId && latestLineageVersionId
    ? `/v5/${encodeURIComponent(canonicalProjectId)}?view=lineage&nodes=${encodeURIComponent(latestLineageVersionId)}`
    : null;

  return (
    <div className="section-campaign-workbench" data-testid="section-campaign-workbench" aria-busy={dispatchState === "submitting"}>
      <aside className="section-shape-panel">
        <div className="section-panel-title">
          <span>01 · geometry</span>
          <strong>Parametric wing</strong>
          <small>VSP-like controls · live geometry preview</small>
        </div>

        <section className="section-control-group">
          <header><span>Planform · lifting-line only</span><code>0 extra 2D rows</code></header>
          <SliderField label="Half span" value={shape.halfSpan} min={1} max={24} step={0.1} unit=" m" testId="planform-half-span" onChange={(value) => setPlanform("halfSpan", value)} />
          <SliderField label="Root chord" value={shape.rootChord} min={0.2} max={8} step={0.05} unit=" m" onChange={(value) => setPlanform("rootChord", value)} />
          <SliderField label="Tip chord" value={shape.tipChord} min={0.1} max={5} step={0.05} unit=" m" onChange={(value) => setPlanform("tipChord", value)} />
          <SliderField label="LE sweep" value={shape.sweepDeg} min={-10} max={55} step={0.25} unit="°" testId="planform-sweep" onChange={(value) => setPlanform("sweepDeg", value)} />
          <SliderField label="Dihedral" value={shape.dihedralDeg} min={-12} max={24} step={0.25} unit="°" onChange={(value) => setPlanform("dihedralDeg", value)} />
          <SliderField label="Tip twist" value={shape.tipTwistDeg} min={-14} max={10} step={0.25} unit="°" onChange={(value) => setPlanform("tipTwistDeg", value)} />
          <dl className="section-planform-metrics">
            <div><dt>Area</dt><dd>{metrics.area.toFixed(1)} m²</dd></div>
            <div><dt>Aspect ratio</dt><dd>{metrics.aspect_ratio.toFixed(2)}</dd></div>
            <div><dt>Sections</dt><dd>{wing.stations.length}</dd></div>
          </dl>
        </section>

        <section className="section-control-group section-airfoil-group">
          <header><span>Section airfoil</span><code>local shape</code></header>
          <label className="section-station-select">
            <span>Active station</span>
            <select value={station.id} onChange={(event) => onSelectStation(event.target.value)}>
              {wing.stations.map((candidate, index) => (
                <option value={candidate.id} key={candidate.id}>{index + 1} · {candidate.id} · {candidate.airfoil}</option>
              ))}
            </select>
          </label>
          <div className="section-station-actions">
            <button type="button" disabled={wing.stations.length >= QUICK_LIFTING_LINE_MAX_STATIONS} onClick={addStation}>＋ Add section</button>
            <button type="button" disabled={wing.stations.length <= 2 || stationIndex === 0} onClick={deleteStation}>Delete</button>
          </div>
          <small className="section-station-limit">
            Quick CPU guard · {estimatedQuickPanels}/{QUICK_LIFTING_LINE_MAX_PANELS} wake panels · {quickSolverWing.span_panels_per_segment}/segment
            {quickSolverWing.span_panels_per_segment < wing.span_panels_per_segment ? ` (auto-coarsened from ${wing.span_panels_per_segment})` : ` · auto-coarsens beyond ${quickStationLimit} stations`}
          </small>
          <div className="section-naca-heading"><span>{station.airfoil}</span><small>4-digit family</small></div>
          <SliderField label="Camber" value={airfoil.camberPercent} min={0} max={9} step={1} unit="%" testId="section-camber" onChange={(value) => setNaca({ camberPercent: value })} />
          <SliderField label="Camber position" value={airfoil.camberPositionTenths} min={0} max={9} step={1} unit="/10c" disabled={airfoil.camberPercent === 0} onChange={(value) => setNaca({ camberPositionTenths: value })} />
          <SliderField label="Thickness" value={airfoil.thicknessPercent} min={6} max={30} step={1} unit="%" onChange={(value) => setNaca({ thicknessPercent: value })} />
          <details className="section-disclosure section-station-details">
            <summary>
              <span>Advanced station geometry</span>
              <small>Position · chord · twist</small>
            </summary>
            <div className="section-disclosure-body">
              <SliderField label="Span station" value={station.y} min={stationYMin} max={stationYMax} step={0.01} unit=" m" disabled={!previousStation || !nextStation} onChange={(value) => setStation("y", value)} />
              <SliderField label="Local LE X" value={station.x_le} min={-4} max={16} step={0.05} unit=" m" onChange={(value) => setStation("x_le", value)} />
              <SliderField label="Local LE Z" value={station.z_le} min={-4} max={8} step={0.05} unit=" m" onChange={(value) => setStation("z_le", value)} />
              <SliderField label="Local chord" value={station.chord} min={0.1} max={8} step={0.05} unit=" m" onChange={(value) => setStation("chord", value)} />
              <SliderField label="Local twist" value={station.twist_deg} min={-14} max={14} step={0.25} unit="°" onChange={(value) => setStation("twist_deg", value)} />
            </div>
          </details>
        </section>
      </aside>

      <section className="section-preview-stage">
        <header>
          <div><span>Geometry → sections</span><strong>{wing.name}</strong></div>
          <div className="section-pipeline-breadcrumb" aria-label="Aero model layers">
            <b className="is-active">geometry</b><i>→</i><b className="is-active">Foil Memory</b><i>→</i><b className={modelPresentation.label !== "not fitted" ? "is-active" : ""}>aircraft NN</b><i>→</i><b className={quickResult ? "is-active" : ""}>wake / LL</b>
          </div>
        </header>
        <div className={`section-preview-canvas${analysisMode !== "wake" ? " is-analysis-open" : ""}`}>
          <WakeScene
            frame={null}
            previewBody={previewBody}
            wakeFilaments={quickResult?.wake_filaments ?? []}
            ariaLabel={quickResult ? "Parametric wing with relaxed free wake" : "Parametric wing geometry preview"}
          />
          <section className="quick-ll-controls" aria-label="Quick lifting-line controls">
            <header>
              <span><strong>Quick 3D</strong><small>aircraft section model → relaxed wake Γ · {estimatedQuickPanels}/{QUICK_LIFTING_LINE_MAX_PANELS} CPU · {quickSolverWing.span_panels_per_segment}/segment</small></span>
              <em>{usableSectionRowCount.toLocaleString()} usable rows · {usableSectionAlphaCount} α</em>
            </header>
            <label>
              <span>Angle of attack</span>
              <output>{quickAlphaDeg.toFixed(1)}°</output>
              <input
                type="range"
                aria-label="Quick lifting-line angle of attack"
                min={-12}
                max={22}
                step={0.5}
                value={quickAlphaDeg}
                onChange={(event) => setQuickAlphaDeg(Number(event.target.value))}
              />
            </label>
            <label>
              <span>Speed</span>
              <output>{quickSpeedMps.toFixed(0)} m/s</output>
              <input
                type="range"
                aria-label="Quick lifting-line speed"
                min={10}
                max={140}
                step={1}
                value={quickSpeedMps}
                onChange={(event) => setQuickSpeedMps(Number(event.target.value))}
              />
            </label>
            <button
              type="button"
              disabled={!quickLiftingLineReady || quickState === "solving" || quickState === "updating"}
              aria-busy={quickState === "solving" || quickState === "updating"}
              onClick={requestQuickLiftingLine}
            >
              {quickState === "solving" ? "Solving…" : quickState === "updating" ? "Updating…" : quickResult ? "Re-run quick 3D" : "Run quick 3D"}
            </button>
            {!quickLiftingLineReady ? (
              <p>
                {canonicalProjectId && sectionEvidenceReady
                  ? modelPersistenceFailed
                    ? "Retry the Thread model save to unlock quick 3D with the latest evidence."
                    : "Saving the canonical aircraft section model before quick 3D…"
                  : "Run at least eight backend-ready 2D rows spanning two angles to unlock quick 3D."}
              </p>
            ) : null}
            {quickMessage ? <p className={`is-${quickState}`} role={quickState === "error" ? "alert" : "status"}>{quickMessage}</p> : null}
          </section>
          {quickResult ? (
            <QuickLiftingLineSummary
              wing={quickSolvedWing ?? wing}
              response={quickResult}
              condition={quickSolvedCondition ?? { alphaDeg: quickAlphaDeg, speedMps: quickSpeedMps }}
              status={quickState === "updating" ? "updating" : quickState === "error" ? "stale" : quickState === "warning" ? "warning" : "current"}
            />
          ) : null}
          <nav className="section-analysis-modebar" aria-label="Section and wake result views">
            {([
              ["wake", "3D wake"],
              ["section-flow", "2D flow"],
              ["cp", "Cp"],
              ["lift", "Lift distribution"],
              ["objective", "Design target"],
              ["compare-3d", "3D compare"],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={analysisMode === value ? "is-active" : undefined}
                aria-pressed={analysisMode === value}
                onClick={() => setAnalysisMode(value)}
              >
                {label}
              </button>
            ))}
          </nav>
          {analysisMode === "objective" ? (
            <AircraftObjectivePanel
              wing={wing}
              sectionRows={sectionTrainingRows}
              alphaDeg={quickAlphaDeg}
              speedMps={quickSpeedMps}
              aircraftModelId={aircraftModelId}
              inputSliderNames={campaignAxesForWing(wing, controls)
                .filter((axis) => axis.role === "section_shape")
                .map((axis) => axis.name)}
              ready={quickLiftingLineReady}
              prepareThreadContext={canonicalProjectId ? prepareThreadInverseDesign : undefined}
              onApplyWing={onWingChange}
            />
          ) : analysisMode === "compare-3d" ? (
            <ThreeDComparisonPanel
              key={wakeWrite?.artifact_version_id ?? "no-persisted-wake"}
              projectId={canonicalProjectId}
              geometryArtifactVersionId={wakeGeometryVersionId}
              predictionArtifactVersionId={wakeWrite?.artifact_version_id ?? null}
              predictionReferenceAreaM2={quickResult?.result.reference_area_m2 ?? null}
              predictionSurfaceIds={[...new Set(quickResult?.horseshoes.map((horseshoe) => horseshoe.surface_id) ?? [])]}
              predictionCurrent={quickState === "done" || quickState === "warning"}
              condition={{
                speed_mps: quickSolvedCondition?.speedMps ?? quickSpeedMps,
                alpha_deg: quickSolvedCondition?.alphaDeg ?? quickAlphaDeg,
                beta_deg: 0,
                density_kg_m3: 1.225,
                dynamic_viscosity_pa_s: 1.81e-5,
                speed_of_sound_mps: 340,
              }}
            />
          ) : (
            <SectionAnalysisPanel
              mode={analysisMode}
              airfoil={station.airfoil}
              chordM={station.chord}
              alphaDeg={quickAlphaDeg}
              speedMps={quickSpeedMps}
              wakeResult={quickResult}
              sectionRows={sectionTrainingRows}
            />
          )}
          <div className="section-preview-legend">
            <span><i /> selected section</span>
            <strong>{station.id}</strong>
            <small>η {(station.y / Math.max(shape.halfSpan, 1e-9)).toFixed(2)} · c {station.chord.toFixed(2)} m · {station.airfoil}</small>
          </div>
        </div>
        <footer>
          <span>
            {canonicalProjectId
              ? <>Thread-persisted study in <b>{projectName}</b> / <b>{branch}</b></>
              : <>Local preview — not saved to Thread · <b>{projectName}</b> / <b>{branch}</b></>}
          </span>
          <code>
            Foil Memory exact{globalInterpolationConfigured ? " / provisional" : ""}
            {" → aircraft section NN from exact evidence → relaxed free wake"}
          </code>
        </footer>
      </section>

      <aside className="section-campaign-panel">
        <div className="section-panel-title">
          <span>02 · sampling</span>
          <strong>Section evidence</strong>
          <small>{sharedFoilMemoryConfigured
            ? `Global exact${globalInterpolationConfigured ? " + bounded provisional reuse" : ""}`
            : "Scoped exact reuse"} · one model per aircraft</small>
        </div>

        <section className="section-model-layers" aria-label="Section data architecture">
          <div>
            <span>{sharedFoilMemoryConfigured ? "Global Foil Memory" : "Local / scoped Foil Memory"}</span>
            <b>{foilMemoryStatus}</b>
            <small>
              Exact 2D solver observations across permitted data
              {memoryStats.shared === false ? " · private scope" : memoryStats.shared === true ? " · shared scope" : ""}
              {memoryStats.storeKind ? ` · ${memoryStats.storeKind}` : ""}
            </small>
            <label className="section-memory-sharing">
              <input
                type="checkbox"
                checked={useGlobalInterpolation}
                disabled={!globalInterpolationConfigured}
                onChange={(event) => setUseGlobalInterpolation(event.target.checked)}
              />
              <span>{globalInterpolationConfigured
                ? "Use bounded global interpolation for unsolved preview points"
                : "Global interpolation needs an enabled durable shared store"}</span>
            </label>
            {useGlobalInterpolation ? (
              <small>
                Provisional rows return immediately, remain outside aircraft-NN training,
                and are queued for an exact solver follow-up. Validation always runs exactly.
              </small>
            ) : null}
            <label className="section-memory-sharing">
              <input
                type="checkbox"
                checked={shareFoilObservations}
                disabled={!sharedFoilPublicationEnabled}
                onChange={(event) => setShareFoilObservations(event.target.checked)}
              />
              <span>{sharedFoilPublicationEnabled
                ? "Contribute new exact solves to shared memory"
                : sharedFoilMemoryConfigured
                  ? "Shared contribution is disabled by deployment policy"
                  : "Shared contribution needs a deployed durable store"}</span>
            </label>
            {capabilities?.foil_memory?.configured === true ? (
              <div className="section-memory-governance">
                <button
                  type="button"
                  disabled={foilContributionBusy}
                  onClick={() => void loadFoilContributions()}
                >
                  {foilContributionBusy ? "Checking…" : foilContributions == null ? "Manage my contributions" : "Refresh contributions"}
                </button>
                {foilContributionError ? <small role="alert">{foilContributionError}</small> : null}
                {foilContributions != null ? (
                  foilContributions.length ? (
                    <ul aria-label="My active Foil Memory contributions">
                      {[...foilContributions]
                        .sort((left, right) => right.contributed_at.localeCompare(left.contributed_at))
                        .map((contribution) => (
                        <li key={contribution.contribution_id}>
                          <code title={contribution.observation_key}>{shortVersion(contribution.observation_key)}</code>
                          <small>{new Date(contribution.contributed_at).toLocaleDateString()}</small>
                          <button
                            type="button"
                            disabled={foilContributionBusy}
                            aria-label={`Withdraw contribution ${shortVersion(contribution.observation_key)}`}
                            onClick={() => void withdrawFoilContribution(contribution.contribution_id)}
                          >Withdraw</button>
                        </li>
                        ))}
                    </ul>
                  ) : <small>No active shared contributions.</small>
                ) : null}
                {foilContributionCursor ? (
                  <button
                    type="button"
                    disabled={foilContributionBusy}
                    onClick={() => void loadFoilContributions(foilContributionCursor)}
                  >Load more</button>
                ) : null}
              </div>
            ) : null}
          </div>
          <i aria-hidden="true">→</i>
          <div>
            <span>Aircraft section NN</span>
            <b>{aircraftModelStatus}</b>
            <small>
              {modelPresentation.instanceId ? `instance ${shortVersion(modelPresentation.instanceId)} · ` : ""}
              {modelPresentation.detail}
            </small>
          </div>
        </section>

        <section className="section-campaign-controls">
          <header><span>Section &amp; flight envelope</span><code>shape · α · Re · M</code></header>
          <div className="section-control-pair">
            <NumberField label="Camber min" value={controls.camberMinPercent} min={0} max={controls.camberMaxPercent} step={0.25} unit="%" onChange={(value) => setControl("camberMinPercent", value)} />
            <NumberField label="Camber max" value={controls.camberMaxPercent} min={controls.camberMinPercent} max={9} step={0.25} unit="%" onChange={(value) => setControl("camberMaxPercent", value)} />
          </div>
          <div className="section-control-pair">
            <NumberField label="Camber x/c min" value={controls.camberPositionMinXc} min={0.05} max={controls.camberPositionMaxXc} step={0.05} onChange={(value) => setControl("camberPositionMinXc", value)} />
            <NumberField label="Camber x/c max" value={controls.camberPositionMaxXc} min={controls.camberPositionMinXc} max={0.9} step={0.05} onChange={(value) => setControl("camberPositionMaxXc", value)} />
          </div>
          <div className="section-control-pair">
            <NumberField label="Thickness min" value={controls.thicknessMinPercent} min={2} max={controls.thicknessMaxPercent} step={0.5} unit="%" onChange={(value) => setControl("thicknessMinPercent", value)} />
            <NumberField label="Thickness max" value={controls.thicknessMaxPercent} min={controls.thicknessMinPercent} max={40} step={0.5} unit="%" onChange={(value) => setControl("thicknessMaxPercent", value)} />
          </div>
          <div className="section-control-pair">
            <NumberField label="Alpha min" value={controls.alphaMin} min={-20} max={controls.alphaMax} step={0.5} unit="deg" onChange={(value) => setControl("alphaMin", value)} />
            <NumberField label="Alpha max" value={controls.alphaMax} min={controls.alphaMin} max={40} step={0.5} unit="deg" onChange={(value) => setControl("alphaMax", value)} />
          </div>
          <div className="section-control-pair">
            <NumberField label="Re min" value={controls.reynoldsMin} min={50_000} max={controls.reynoldsMax} step={50_000} onChange={(value) => setControl("reynoldsMin", Math.max(50_000, value))} />
            <NumberField label="Re max" value={controls.reynoldsMax} min={controls.reynoldsMin} max={50_000_000} step={100_000} onChange={(value) => setControl("reynoldsMax", Math.max(controls.reynoldsMin, value))} />
          </div>
          <div className="section-control-pair">
            <NumberField label="Mach min" value={controls.machMin} min={0} max={controls.machMax} step={0.01} onChange={(value) => setControl("machMin", value)} />
            <NumberField label="Mach max" value={controls.machMax} min={controls.machMin} max={0.95} step={0.01} onChange={(value) => setControl("machMax", value)} />
          </div>
        </section>

        <details className="section-disclosure section-sampling-details">
          <summary>
            <span>Sampling &amp; budget</span>
            <small>{displayedGroupCount} sweeps · {displayedRowsPerSweep} rows each</small>
          </summary>
          <div className="section-disclosure-body">
            <section className="section-input-summary" aria-label="Section model inputs">
              <span><small>shape</small><b>m · p · t/c</b><em>joint Halton DOE</em></span>
              <span><small>α</small><b>{controls.alphaMin}…{controls.alphaMax}°</b><em>{controls.alphaSamples} endpoint samples</em></span>
              <span><small>Re</small><b>{compact(controls.reynoldsMin / 1e6, 1)}…{compact(controls.reynoldsMax / 1e6, 1)}M</b><em>log10 bound</em></span>
              <span><small>Mach</small><b>{compact(controls.machMin)}…{compact(controls.machMax)}</b><em>linear bound</em></span>
            </section>
            <div className="section-sampling-controls">
              <SliderField label="DOE groups" value={controls.baseDesignPoints} min={8} max={240} step={4} onChange={(value) => setControl("baseDesignPoints", Math.round(value))} />
              <SliderField label="Alpha samples" value={controls.alphaSamples} min={2} max={61} step={1} onChange={(value) => setControl("alphaSamples", Math.round(value))} />
            </div>
            <div className="section-scope-note">
              <span>Excluded from 2D solve cost</span>
              <strong>span · root/tip chord · sweep · dihedral · twist</strong>
              <small>Planform axes are retained for the downstream lifting-line sweep; only section shape, α, Re and Mach buy 2D rows.</small>
            </div>
            <dl className="section-sampling-metrics">
              <div><dt>Halton groups</dt><dd>{displayedGroupCount}</dd></div>
              <div><dt>Rows / sweep</dt><dd>{displayedRowsPerSweep}</dd></div>
              <div><dt>2D axes</dt><dd>6</dd></div>
            </dl>
            {!usesExternalAdapter ? (
              <small className="section-pilot-cap">
                Every solve request is capped at {batchLimit} rows{capabilities ? ` by the ${capabilities.section_solver.transport} solver capability` : " until server capabilities load"}.
                Single-batch and sequential run modes query Foil Memory before buying a new solve.
              </small>
            ) : null}
            {serverPlan ? (
              <div className="section-server-budget">
                <span>ML budget after planner</span>
                <b>{serverPlan.training_budget.total_likely.toLocaleString()} likely</b>
                <small>{serverPlan.training_budget.total_low.toLocaleString()}–{serverPlan.training_budget.total_high.toLocaleString()} rows · {serverPlan.training_budget.method.replace(/_/g, " ")}</small>
              </div>
            ) : null}
          </div>
        </details>

        {uncoveredStations.length ? (
          <div className="section-domain-warning" role="alert">
            <strong>Planned section envelope misses {uncoveredStations.length} wing {uncoveredStations.length === 1 ? "station" : "stations"}</strong>
            {uncoveredStations.map((message) => <span key={message}>{message}</span>)}
          </div>
        ) : (
          <div className="section-domain-ok"><i />All {wing.stations.length} station airfoils lie inside the planned section envelope.</div>
        )}

        <section className="section-server-budget" data-testid="thread-aero-lineage" aria-label="Thread aero lineage state">
          {canonicalProjectId ? (
            <>
              <span>{lineageTargets ? `Campaign ${shortVersion(lineageTargets.campaignId)}` : "Thread persistence ready"}</span>
              <b>
                {campaignGeometryWrite ? `geometry v${shortVersion(campaignGeometryWrite.artifact_version_id)}` : "geometry pending"}
                {planWrite ? ` · plan v${shortVersion(planWrite.artifact_version_id)}` : ""}
                {latestBatchWrite ? ` · evidence v${shortVersion(latestBatchWrite.artifact_version_id)}` : ""}
                {modelWrite ? ` · model v${shortVersion(modelWrite.artifact_version_id)}` : ""}
                {wakeWrite ? ` · run v${shortVersion(wakeWrite.artifact_version_id)}` : ""}
              </b>
              <small>
                {[campaignGeometryWrite, planWrite, latestBatchWrite, modelWrite, wakeWrite].some((write) => write?.replayed)
                  ? "Thread lineage saved · one or more operations replayed"
                  : latestLineageVersionId ? "Thread lineage saved" : "The first campaign batch creates canonical versions"}
                {lineageHref ? <> · <a href={lineageHref}>View lineage</a></> : null}
              </small>
            </>
          ) : (
            <>
              <span>Persistence</span>
              <b>Local preview — not saved to Thread</b>
              <small>Open this workbench inside a persisted Thread project to create campaign lineage.</small>
            </>
          )}
          {persistenceError ? <small className="is-error" role="status">{persistenceError}</small> : null}
          {canonicalProjectId && sectionEvidenceReady && modelPersistenceFailed ? (
            <button type="button" className="is-secondary" disabled={modelRetrying || dispatchState === "submitting"} onClick={() => void retryModelPersistence()}>
              {modelRetrying ? "Saving model…" : "Retry model save"}
            </button>
          ) : null}
        </section>

        <section className="section-campaign-estimate">
          <div>
            <span>{serverPlan ? "Planned dataset rows" : "Estimated training rows"}</span>
            <strong data-testid="training-row-estimate">{(serverPlan?.plan.unique_2d_solve_count ?? plan.estimatedRows).toLocaleString()}</strong>
            {datasetRowSummary.length ? (
              <small data-testid="dataset-row-splits">
                {datasetRowSummary.map(([split, count]) => `${count.toLocaleString()} ${split}`).join(" · ")}
              </small>
            ) : null}
          </div>
          <div className="section-campaign-actions">
            <button
              type="button"
              className="is-primary"
              disabled={dispatchState === "submitting" || (!usesExternalAdapter && campaignComplete)}
              aria-busy={activeRunMode === "single"}
              onClick={() => void queueCampaign("single")}
            >
              {activeRunMode === "single" ? "Running batch…" : activeRunMode === "remaining" ? "Running full campaign…" : singleRunLabel}
              <span>→</span>
            </button>
            {!usesExternalAdapter ? activeRunMode === "remaining" ? (
              <button type="button" className="is-secondary" onClick={requestAutoStop} disabled={stopRequested}>
                {stopRequested ? "Pausing after batch…" : "Pause after current batch"}
              </button>
            ) : (
              <button
                type="button"
                className="is-secondary"
                disabled={dispatchState === "submitting" || campaignComplete}
                onClick={() => void queueCampaign("remaining")}
              >
                {serverPlan ? "Run remaining campaign" : "Run full campaign"}
              </button>
            ) : null}
          </div>
          {dispatchMessage ? (
            <p
              className={`is-${dispatchState}`}
              role={dispatchState === "error" ? "alert" : "status"}
              aria-live={dispatchState === "error" ? "assertive" : "polite"}
            >
              {dispatchMessage}
            </p>
          ) : null}
          {pilotResult ? (
            <div className="section-pilot-summary">
              <span>
                <strong>{resultStats.executedRows} executed · {resultStats.trainableRows} trainable</strong>
                <small>
                  {resultStats.completedRows} completed · {resultStats.validationRows} validation held out · {resultStats.provisionalRows} provisional awaiting exact · {resultStats.nonConvergedRows} non-trainable fit rows · {memoryStats.globalExactHits} global exact
                  {memoryStats.globalApproximateHits ? ` · ${memoryStats.globalApproximateHits} global approximate` : ""}
                  {memoryStats.exactReuseHits ? ` · ${memoryStats.exactReuseHits} scoped exact` : ""}
                  {memoryStats.legacyExactHits ? ` · ${memoryStats.legacyExactHits} legacy cache` : ""}
                </small>
              </span>
              <button type="button" onClick={downloadPilotJson}>Download JSON</button>
            </div>
          ) : null}
          {pilotResult?.failed ? (
            <div className="section-pilot-failures" role="alert">
              <strong>{pilotResult.failed} pilot {pilotResult.failed === 1 ? "failure" : "failures"} retained as data</strong>
              {pilotResult.rows.filter((row) => row.status === "error").slice(0, 4).map((row) => (
                <span key={row.point_id}>
                  <code>{row.point_id}</code>
                  <em className={row.retryable || row.error_kind === "transport" ? "is-retryable" : "is-deterministic"}>
                    {row.retryable || row.error_kind === "transport" ? "retryable infrastructure" : row.error_kind === "solver" ? "deterministic solver" : "unclassified"}
                  </em>
                  <small>{row.error ?? "section solver failed"}</small>
                </span>
              ))}
            </div>
          ) : null}
          {predictedRows.length ? (
            <details className="section-disclosure section-pilot-success">
              <summary>
                <span>Result rows</span>
                <small>{predictedRows.length} available · source / Cl / Cd / Cm / quality</small>
              </summary>
              <div className="section-disclosure-body">
                <div className="section-pilot-table" role="table" aria-label="Executed section rows">
                  <div role="row" className="is-heading"><span role="columnheader">point</span><span role="columnheader">source</span><span role="columnheader">Cl</span><span role="columnheader">Cd</span><span role="columnheader">Cm</span><span role="columnheader">quality</span></div>
                  {predictedRows.slice(0, 8).map((row) => {
                    const source = sectionRowSource(row);
                    return (
                      <div role="row" key={row.point_id}>
                        <code role="cell">{row.point_id}</code>
                        <span role="cell" className={source.isGlobalExact ? "is-global-exact" : undefined} title={source.detail ?? undefined}>{source.label}</span>
                        <span role="cell">{row.prediction!.cl.toFixed(3)}</span>
                        <span role="cell">{row.prediction!.cd.toFixed(4)}</span>
                        <span role="cell">{row.prediction!.cm.toFixed(3)}</span>
                        <span role="cell" className={row.prediction!.converged ? "is-converged" : "is-unconverged"}>{Math.round(row.prediction!.convergence_quality * 100)}%</span>
                      </div>
                    );
                  })}
                </div>
                {predictedRows.length > 8 ? <small>Showing 8 of {predictedRows.length}; the export retains every attempt, success, and failure.</small> : null}
              </div>
            </details>
          ) : null}
        </section>
      </aside>

      <section className="section-coverage-panel">
        <header>
          <div><span>03 · progress</span><strong>Campaign coverage</strong></div>
          <div className="section-coverage-totals">
            <span><i className="is-trainable" /> {trainableRows}/{plannedRowCount} trainable</span>
            {runningRows ? <span><i className="is-running" /> {runningRows} running</span> : null}
            {failedRows ? <span><i className="is-failed" /> {failedRows} failed</span> : null}
            <b>{coveragePercent}%</b>
          </div>
        </header>
        <div className="section-coverage-compact">
          <div className="section-coverage-progress" aria-label={`${coveragePercent}% trainable coverage`}>
            <i style={{ width: `${coveragePercent}%` }} />
          </div>
          <span>{completedRows} completed · {freshRows} new solves · {cacheHitRows} exact reuse</span>
        </div>
        <details className="section-disclosure section-coverage-details">
          <summary>
            <span>Sweep coverage</span>
            <small>{coverageGroups.length} groups · {coverageBatches.length} batches</small>
          </summary>
          <div className="section-coverage-body">
            <div
              className="section-sweep-groups"
              data-testid="section-coverage-batches"
              aria-label={`${coverageBatches.length} campaign batches covering ${coverageGroups.length} alpha sweep groups`}
            >
              {coverageBatches.map((summary) => (
                <span key={summary.firstGroup}>
                  <b>groups {summary.firstGroup}–{summary.lastGroup}</b>
                  <small>
                    {summary.groupCount} joint Halton points · {summary.rowCount} α rows
                    {summary.datasetSplits.length ? ` · ${summary.datasetSplits.join("/")} split` : ""}
                  </small>
                </span>
              ))}
            </div>
            <div
              className="section-coverage-map"
              data-testid="section-coverage-map"
              role="list"
              aria-label={`${coveragePercent}% trainable section campaign coverage across ${coverageGroups.length} alpha sweep groups`}
            >
              {coverageGroupsWithOffsets.map((group) => {
                let state: "trainable" | "completed" | "running" | "partial" | "failed" | "planned" | "empty";
                if (serverPlan) {
                  const groupRows = group.pointIds
                    .map((pointId) => resultRowsByPointId.get(pointId))
                    .filter((row) => row != null);
                  const groupIsRunning = group.pointIds.some((pointId) => runningPointIdSet.has(pointId));
                  const groupTrainableRows = groupRows.filter(
                    (row) => row.status === "done" && row.prediction?.converged === true,
                  ).length;
                  const groupCompletedRows = groupRows.filter((row) => row.status === "done").length;
                  if (groupIsRunning) state = "running";
                  else if (group.rowCount > 0 && groupTrainableRows === group.rowCount) state = "trainable";
                  else if (group.rowCount > 0 && groupCompletedRows === group.rowCount) state = "completed";
                  else if (groupRows.length === group.rowCount && groupRows.every((row) => row.status === "error")) state = "failed";
                  else if (groupRows.length > 0) state = "partial";
                  else state = "planned";
                } else {
                  state = group.startRow < trainableRows
                    ? "trainable"
                    : group.startRow < trainableRows + runningRows
                      ? "running"
                      : dispatchState === "staged"
                        ? "planned"
                        : "empty";
                }
                const splitLabel = group.datasetSplit === "unassigned" ? "split unassigned" : `${group.datasetSplit} split`;
                const groupLabel = `Alpha sweep group ${group.designIndex + 1} · ${group.rowCount} endpoint-inclusive α rows · ${splitLabel} · ${state}`;
                return (
                  <i
                    key={group.id}
                    className={`is-${state}`}
                    role="listitem"
                    aria-label={groupLabel}
                    data-dataset-split={group.datasetSplit}
                    title={groupLabel}
                  />
                );
              })}
            </div>
          </div>
        </details>
      </section>
    </div>
  );
}
